import { useMemo } from 'react';
import { useBulkActionsReporting } from '../use-bulk-actions-reporting/use-bulk-actions-reporting';
import type { BulkActionMenuItem } from '../../providers/bulk-actions-provider/bulk-actions-provider.component';

/**
 * Hook to retrieve a bulk action by ID with optional bound parameters.
 *
 * @param id - The unique identifier of the bulk action
 * @param boundArgs - Optional parameters to bind to the action. When the returned
 *                    action is invoked, these will be passed as arguments.
 *
 * @example
 * // Without parameters - action() takes no args
 * const exportAction = useBulkAction('export-all-entities');
 *
 * @example
 * // With parameters - action() will be called with selectedEntities
 * const publishAction = useBulkAction('bulk-publish-selected-entities', selectedEntities);
 *
 * Actions that exist once per promotion target don't have a fixed id -- reach for
 * {@link useBulkActionsForBaseId} instead.
 */

export const useBulkAction = <TArgs extends unknown[]>(
  id: string,
  ...boundArgs: TArgs
): BulkActionMenuItem | undefined => {
  const { actions } = useBulkActionsReporting();

  const baseAction = actions.find((a) => a.id === id);

  return useMemo(() => {
    if (!baseAction) {
      // eslint-disable-next-line no-console
      console.error(`Bulk action with id ${id} not found`);
      return undefined;
    }

    // If no args provided, return the action as-is
    if (boundArgs.length === 0) {
      return baseAction;
    }

    // notify if the passed action is not a function
    if (typeof baseAction.action !== 'function') {
      // eslint-disable-next-line no-console
      console.error(`Bulk action with id ${id} is not a function`);
      return undefined;
    }

    // Return a new action with the args pre-bound
    return {
      ...baseAction,
      action: () => baseAction.action?.(...boundArgs),
    };
    // The spread in deps is intentional - we want to recompute when any arg changes.
    // ESLint can't statically verify spreads but it's functionally correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseAction, ...boundArgs]);
};
